import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  QueueFilters,
  QueueResponse,
  ReviewDecision,
  StatsResponse,
} from '@/client/types/areaEvents';

const BASE = '/api/admin/v2/area-events';

const qs = (params: Record<string, string | number | undefined>) => {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

export const areaEventKeys = {
  all: ['area-events'] as const,
  queue: (params: QueueFilters) => [...areaEventKeys.all, 'queue', params] as const,
  stats: () => [...areaEventKeys.all, 'stats'] as const,
};

/** Admin v2 wraps payloads in `{ status, data }`; accept either shape. */
const unwrap = <T>(raw: unknown): T => {
  if (raw != null && typeof raw === 'object' && 'data' in raw && 'status' in raw) {
    return (raw as { data: T }).data;
  }
  return raw as T;
};

const useGet = () => {
  const request = useClientStore((s) => s.request);
  const fn = withErrorHandling(
    async (path: string): Promise<unknown> =>
      unwrap<unknown>(await request<unknown>(`${BASE}${path}`)),
    'Failed to load place history',
  );
  // withErrorHandling erases generics; restore the typed call.
  return fn as unknown as <T>(path: string) => Promise<T>;
};

export const useAreaEventQueueQuery = (params: QueueFilters, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: areaEventKeys.queue(params),
    queryFn: () => get<QueueResponse>(`/queue${qs({ ...params })}`),
    enabled,
    staleTime: 1000 * 30,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useAreaEventStatsQuery = (enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: areaEventKeys.stats(),
    queryFn: () => get<StatsResponse>('/stats'),
    enabled,
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** One decision for one or many events. Every read is refreshed afterwards. */
export const useReviewMutation = () => {
  const request = useClientStore((s) => s.request);
  const queryClient = useQueryClient();
  const fn = withErrorHandling(
    async (vars: { ids: number[]; status: ReviewDecision }): Promise<{ changed: number }> =>
      unwrap<{ changed: number }>(await request<unknown>(`${BASE}/review`, 'POST', vars)),
    'Failed to record the decision',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: areaEventKeys.all }),
  });
};
